import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Heart, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
  Phone,
  MapPin,
  Eye,
  Stethoscope,
  Target,
  Award,
  Search,
  Microscope,
  Scan,
  Bone,
  Sparkles
} from 'lucide-react';
import { hospitalInfo } from '../mock';
import { useBusinessInfo } from '../hooks/useBusinessInfo';
import { useCMSPage } from '../hooks/useCMSPage';
import InnerPageLayout from '../components/InnerPageLayout';

const DentalCleanings = () => {
  const location = useLocation();
  const { pageData, loading: cmsLoading } = useCMSPage(location.pathname);
  const { businessInfo: currentBusinessInfo } = useBusinessInfo();
  const primaryColor = 'var(--theme-primary)';
  const primaryBg = 'var(--theme-primary-light)';

  // Use CMS data if available, otherwise use defaults
  const title = pageData?.title || "Dental Cleaning";
  const badgeText = pageData?.badge_text || "Professional Oral Health Care";
  const description = pageData?.description || "Does your pet have bad breath, discolored teeth, or tartar buildup? Just like people, pets need regular dental care to protect their health. At Pets and Vets Animal Hospital & Urgent Care, we provide safe, thorough dog and cat dental cleanings in Chantilly, VA.";

  const cleaningProcedures = [
    {
      procedure: "Plaque & Tartar Removal",
      description: "Comprehensive removal above and below the gumline for complete oral health",
      details: ["Surface plaque elimination", "Deep tartar removal", "Subgingival cleaning", "Bacteria elimination"],
      icon: Sparkles,
      color: "#10b981"
    },
    {
      procedure: "Dental Examination",
      description: "Thorough probing and charting of teeth to identify signs of disease",
      details: ["Periodontal probing", "Tooth charting", "Disease assessment", "Mobility evaluation"],
      icon: Search,
      color: "#f59e0b"
    },
    {
      procedure: "Dental X-Rays",
      description: "Advanced imaging to detect hidden dental issues below the gumline",
      details: ["Root examination", "Bone assessment", "Hidden cavity detection", "Structural analysis"],
      icon: Scan,
      color: "#8b5cf6"
    },
    {
      procedure: "Polishing & Prevention",
      description: "Professional polishing and preventive treatments for ongoing oral health",
      details: ["Tooth polishing", "Surface smoothing", "Preventive treatments", "Future buildup prevention"],
      icon: Award,
      color: primaryColor
    },
    {
      procedure: "Comprehensive Oral Exam",
      description: "Complete examination of all oral structures for abnormalities",
      details: ["Gum assessment", "Lip examination", "Tongue evaluation", "Oral tissue check"],
      icon: Eye,
      color: "#ef4444"
    }
  ];

  const warningSignsData = [
    { sign: "Bad Breath", description: "Persistent halitosis often indicates bacterial buildup and dental disease", severity: "early", icon: AlertTriangle },
    { sign: "Brown or Gold Tartar", description: "Visible tartar buildup near the gums requiring professional removal", severity: "moderate", icon: Target },
    { sign: "Red or Bleeding Gums", description: "Inflammation and bleeding indicating gingivitis or periodontal disease", severity: "serious", icon: Heart },
    { sign: "Excessive Drooling or Pawing", description: "Behavioral changes indicating oral pain or discomfort", severity: "serious", icon: Activity }
  ];

  const healthImpacts = [
    { impact: "Oral Pain Prevention", description: "Eliminates painful dental disease and maintains comfortable eating", icon: Shield },
    { impact: "Tooth Preservation", description: "Prevents tooth loss through early intervention and proper care", icon: Bone },
    { impact: "Infection Control", description: "Removes bacteria that can cause local and systemic infections", icon: Microscope },
    { impact: "Organ Protection", description: "Prevents dental bacteria from affecting heart, liver, and kidneys", icon: Stethoscope }
  ];

  const serviceAreas = ["Chantilly", "South Riding", "Aldie", "Ashburn", "Centreville", "Reston", "Herndon"];
  const qualityOfLifeBenefits = ["Fresher breath and improved comfort", "Better eating and nutrition absorption", "Prevention of painful dental complications", "Enhanced overall health and longevity"];

  // Hero content for the left side
  const heroContent = (
    <div className="text-center lg:text-left">
      <h1 className="text-xl font-bold text-gray-900 mb-4">{title}</h1>
      <div className="mb-4">
        <span className="inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold text-white" style={{ backgroundColor: primaryColor }}>
          {badgeText}
        </span>
      </div>
      <p className="text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );

  // Render CMS main content if available
  const renderMainContent = () => {
    if (pageData?.main_content) {
      const paragraphs = pageData.main_content.split('\n\n').filter(p => p.trim());
      return (
        <section className="bg-[#f8f9fa] py-6">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-4">
              {paragraphs.map((paragraph, index) => (
                <p key={index} className="text-base text-gray-600 leading-relaxed text-center">{paragraph}</p>
              ))}
            </div>
          </div>
        </section>
      );
    }
    return null;
  };

  const hasCMSContent = pageData?.main_content && pageData.main_content.trim().length > 0;

  return (
    <InnerPageLayout heroContent={heroContent} customImage={pageData?.hero_image}>
    <div className="bg-gray-50">
      {hasCMSContent ? (
        renderMainContent()
      ) : (
        <>
          {/* Why Dental Cleanings Matter */}
          <section className="bg-[#f8f9fa] py-6">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">Why Dental Cleanings Matter</h2>
              <p className="text-center text-gray-600 mb-6 text-sm">
                Poor dental health can lead to pain, tooth loss, infection, and even affect your pet's heart, liver, 
                and kidneys. Professional cleanings remove harmful plaque and bacteria, protecting both your pet's 
                mouth and overall health.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {healthImpacts.map((impact, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg shadow-sm text-center">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-3" style={{ backgroundColor: primaryBg }}>
                      <impact.icon className="h-6 w-6" style={{ color: primaryColor }} />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">{impact.impact}</h3>
                    <p className="text-gray-600 text-sm">{impact.description}</p>
                  </div>
                ))}
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                <div className="flex items-center mb-3">
                  <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                  <h3 className="text-base font-semibold text-red-900">Critical Health Connection</h3>
                </div>
                <p className="text-red-800 text-sm">
                  Dental bacteria can enter your pet's bloodstream and travel to vital organs, potentially causing 
                  serious health complications. Professional dental cleanings are essential preventive care.
                </p>
              </div>
            </div>
          </section>

          {/* What a Pet Dental Cleaning Involves */}
          <section className="bg-white py-6">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">What a Pet Dental Cleaning Involves</h2>
              <p className="text-center text-gray-600 mb-6 text-sm">Our comprehensive cleanings include:</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                {cleaningProcedures.map((procedure, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded-lg shadow-sm">
                    <div className="flex items-start space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: `${procedure.color}15` }}>
                        <procedure.icon className="h-5 w-5" style={{ color: procedure.color }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">{procedure.procedure}</h3>
                        <p className="text-gray-600 text-xs mb-2">{procedure.description}</p>
                        <div className="grid grid-cols-1 gap-1">
                          {procedure.details.map((detail, detailIndex) => (
                            <div key={detailIndex} className="flex items-center text-xs">
                              <CheckCircle className="h-2 w-2 mr-1 flex-shrink-0" style={{ color: procedure.color }} />
                              <span className="text-gray-600">{detail}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-theme-blue">
                <div className="flex items-center mb-3">
                  <Shield className="h-5 w-5 mr-2 text-theme-blue" />
                  <h3 className="text-base font-semibold text-blue-900">Safe Anesthesia Protocol</h3>
                </div>
                <p className="text-blue-800 text-sm">
                  Because a complete cleaning can only be done safely under anesthesia, your pet will be kept pain-free 
                  and comfortable throughout the procedure. Our experienced team monitors your pet closely to ensure 
                  maximum safety and comfort.
                </p>
              </div>
            </div>
          </section>

          {/* Signs Your Pet May Need a Cleaning */}
          <section className="bg-[#f8f9fa] py-6">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">Signs Your Pet May Need a Cleaning</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {warningSignsData.map((warning, index) => (
                  <div key={index} className={`bg-white p-4 rounded-lg shadow-sm border-l-4 ${
                    warning.severity === 'early' ? 'border-yellow-500' : warning.severity === 'moderate' ? 'border-orange-500' : 'border-red-500'
                  }`}>
                    <div className="flex items-center mb-3">
                      <warning.icon className={`h-5 w-5 mr-2 ${
                        warning.severity === 'early' ? 'text-yellow-600' : warning.severity === 'moderate' ? 'text-orange-600' : 'text-red-600'
                      }`} />
                      <h3 className="text-base font-semibold text-gray-900">{warning.sign}</h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-2">{warning.description}</p>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      warning.severity === 'early' ? 'bg-yellow-100 text-yellow-800' : warning.severity === 'moderate' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {warning.severity === 'early' ? 'Early Sign' : warning.severity === 'moderate' ? 'Moderate Concern' : 'Serious Concern'}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                <div className="flex items-center mb-3">
                  <Clock className="h-5 w-5 mr-2 text-orange-600" />
                  <h3 className="text-base font-semibold text-orange-900">Early Intervention is Key</h3>
                </div>
                <p className="text-orange-800 text-sm">
                  If you notice these symptoms, your pet may already have dental disease. Early care helps prevent 
                  painful complications and more extensive treatments.
                </p>
              </div>
            </div>
          </section>

          {/* Healthier Mouth, Healthier Pet */}
          <section className="bg-white py-6">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Healthier Mouth, Healthier Pet</h2>
              <div className="p-6 rounded-lg" style={{ backgroundColor: primaryBg }}>
                <p className="text-gray-700 leading-relaxed mb-4" style={{ fontSize: '1rem' }}>
                  A professional dental cleaning doesn't just freshen breath—it prevents infection, saves teeth, and 
                  improves your pet's quality of life. Regular dental care is an investment in your pet's long-term 
                  health and happiness.
                </p>
                <div className="bg-white p-4 rounded-lg">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Quality of Life Benefits:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {qualityOfLifeBenefits.map((benefit, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircle className="h-3 w-3 mr-2 flex-shrink-0" style={{ color: primaryColor }} />
                        <span className="text-xs text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Service Areas */}
          <section className="bg-[#f8f9fa] py-6">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Serving Your Community</h2>
              <p className="text-gray-600 mb-6 text-sm">We proudly provide professional dental cleaning services to pet families across:</p>
              <div className="flex flex-wrap justify-center gap-3">
                {serviceAreas.map((area, index) => (
                  <span key={index} className="px-4 py-2 rounded-full text-white font-medium" style={{ backgroundColor: primaryColor }}>{area}</span>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Contact CTA */}
      <section style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, var(--theme-primary-hover) 100%)` }} className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bold text-white mb-4" style={{ fontSize: '1rem' }}>Schedule Your Pet's Dental Cleaning Today</h2>
          <p className="mb-6" style={{ color: 'white', fontSize: '1rem' }}>Professional dental care for healthier teeth, fresher breath, and better overall health</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${currentBusinessInfo.phone}`} className="inline-flex items-center justify-center bg-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200" style={{ color: primaryColor }}>
              <Phone className="mr-2 h-5 w-5" />Call Now: {currentBusinessInfo.phone}
            </a>
            <Link to="/reach-us" className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 hover:bg-white hover:text-theme-primary">
              Schedule Dental Cleaning<MapPin className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </InnerPageLayout>
  );
};

export default DentalCleanings;